/**
 * useRole — fetch admin role from /api/admin/me
 * Returns 'viewer' by default if unauthenticated; UI guards on this.
 */

import { useQuery } from '@tanstack/react-query';
import type { AdminRole } from './admin-api';

const BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? window.location.origin;

interface MeResponse {
  id?: string;
  email?: string;
  role: AdminRole;
}

async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch(`${BASE}/api/admin/me`, { credentials: 'include' });
  if (!res.ok) return null;
  return res.json();
}

export function useRole() {
  const q = useQuery({
    queryKey: ['admin-me'],
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const role = (q.data?.role ?? 'viewer') as AdminRole;
  const rank: Record<AdminRole, number> = { viewer: 1, operator: 2, admin: 3, owner: 4 };
  function can(min: AdminRole): boolean {
    return rank[role] >= rank[min];
  }
  return {
    isLoading: q.isLoading,
    role,
    email: q.data?.email ?? null,
    can,
    canWrite: can('operator'),
    canAdmin: can('admin'),
    canOwn: can('owner'),
  };
}
